import { createContext, useState, useEffect, ReactNode } from 'react';
import { PushNotificationService } from '../services/pushNotificationService';
import { analytics } from '../services/analyticsService';
import i18n from '../i18n';
import { I18nLang, User } from '../models/user';
import { apiGet } from '../services/apiClient';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiEndpoints';

interface AuthContextType {
  isLoggedIn: boolean;
  userEmail: string | null;
  handleLogin: (email: string, token: string) => void;
  handleLogout: () => void;
  isLoading: boolean;
  pushNotificationsEnabled: boolean;
  enablePushNotifications: () => Promise<boolean>;
  disablePushNotifications: () => Promise<boolean>;
  user: User | null;
  setUserFetchCounter: (value: React.SetStateAction<number>) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
  const [userFetchCounter, setUserFetchCounter] = useState(0);
  const [user, setUser] = useState<User | null>(null);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    // 1) On mount: read localStorage
    const storedAuth = localStorage.getItem('isAuthenticated') === 'true';
    const storedEmail = localStorage.getItem('userEmail');
    if (storedAuth && storedEmail) {
      setIsLoggedIn(true);
      setUserEmail(storedEmail);

      apiGet(buildApiUrl(API_ENDPOINTS.users.info)).then(response => response.json()).then(({result}) => {
        if (result) {
          setUser(result);
          
          // Set tenant ID for analytics tracking
          if (result.tenant_id) {
            analytics.setTenantId(result.tenant_id);
            analytics.setUserId(result.id?.toString() || null);
          }
          
          const lang = I18nLang[result.language || I18nLang.EN].toLocaleLowerCase();
          i18n.changeLanguage(lang);
          // TODO: pass via context instead of localStorage
          localStorage.setItem('notea-lang', lang);
        }
      }).catch((error) => {
        // Silently fail - user language is not critical for app functionality
        console.error('Failed to fetch user language:', error);
      });
    }
    setIsLoading(false);
  }, [userFetchCounter]);

  // Set up event listeners for logout - separate effect so it's always active
  useEffect(() => {
    // Listen for runtime changes in other tabs/devtools
    function onStorage(e: StorageEvent) {
      if (e.key === 'isAuthenticated' && e.newValue !== 'true') {
        setIsLoggedIn(false);
        setUserEmail(null);
      }
    }
    window.addEventListener('storage', onStorage);

    // 3) Listen for auth logout events from apiClient (same-tab logout)
    function onAuthLogout() {
      setIsLoggedIn(false);
      setUserEmail(null);
      setPushNotificationsEnabled(false);
      // Clear analytics user data
      analytics.setTenantId(null);
      analytics.setUserId(null);
    }
    window.addEventListener('auth:logout', onAuthLogout);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth:logout', onAuthLogout);
    };
  }, []);

  // Check push notification status when user logs in
  useEffect(() => {
    if (isLoggedIn && !isLoading) {
      checkPushNotificationStatus();
    }
  }, [isLoggedIn, isLoading]);

  const checkPushNotificationStatus = async () => {
    if (PushNotificationService.isSupported()) {
      const subscription = await PushNotificationService.getCurrentSubscription();
      setPushNotificationsEnabled(!!subscription);
    }
  };

  const handleLogin = (email: string, token: string) => {
    try {
      setIsLoggedIn(true);
      setUserEmail(email);
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userEmail', email);
      localStorage.setItem('token', token);
      setUserFetchCounter(prev => prev + 1);
    } catch (_) {
      // Still update state even if localStorage fails (for session-only auth)
      setIsLoggedIn(true);
      setUserEmail(email);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserEmail(null);
    setPushNotificationsEnabled(false);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('token');
    setUserFetchCounter(prev => prev + 1);
  };

  const enablePushNotifications = async (): Promise<boolean> => {
    try {
      const success = await PushNotificationService.subscribeToNotifications(user!.server);
      if (success) {
        setPushNotificationsEnabled(true);
      }
      return success;
    } catch (_) {
      return false;
    }
  };

  const disablePushNotifications = async (): Promise<boolean> => {
    try {
      const success = await PushNotificationService.unsubscribeFromNotifications();
      if (success) {
        setPushNotificationsEnabled(false);
      }
      return success;
    } catch (_) {
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      userEmail,
      handleLogin,
      handleLogout,
      isLoading,
      pushNotificationsEnabled,
      enablePushNotifications,
      disablePushNotifications,
      user,
      setUserFetchCounter
    }}>
      {children}
    </AuthContext.Provider>
  );
};
